import React, { useEffect, useState } from "react";
import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import { getAPICall } from "lib/callApi";
import { FETCH_COMPANY_LIST, FETCH_HIERARCHY } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import TooltipLight from "utils/TooltipLight";

import { Tree, TreeNode } from "react-organizational-chart";

function getRandomHexColor(str) {
  let hash = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    // eslint-disable-next-line no-bitwise
    hash &= hash;
  }
  // eslint-disable-next-line no-bitwise
  const colorValue = hash & 0x00ffffff;

  const baseHex = colorValue.toString(16).padStart(6, "0");

  return `#${baseHex}80`;
}

const createTreeFromHierarchy = (data) => {
  const calculatedTree = {};

  const finalTree = {
    rootNode: null,
    teamColorScheme: {
      Unassigned: "#FFFFFF00",
    },
    noUpline: [],
  };

  const teamColors = {};

  const visuallyDistinctColors = [
    "rgba(255, 87, 51, 0.3)",
    "rgba(52, 152, 219, 0.3)",
    "rgba(46, 204, 113, 0.3)",
    "rgba(155, 89, 182, 0.3)",
    "rgba(241, 196, 15, 0.3)",
    "rgba(231, 76, 60, 0.3)",
    "rgba(26, 188, 156, 0.3)",
    "rgba(52, 73, 94, 0.3)",
    "rgba(243, 156, 18, 0.3)",
    "rgba(211, 84, 0, 0.3)",
  ];

  const genPermissionTooltip = (userCompanyLinkId) =>
    Object.entries(data.companyLinkPermissionDict[userCompanyLinkId])
      .map(([permissionId, allow]) => {
        if (allow) {
          return `${data.permissionsDict[permissionId].displayName}`;
        }
        return null;
      })
      .filter((val) => val)
      .sort()
      .reduce((strings, permissionString) => {
        strings.push(permissionString);

        return strings;
      }, []);

  Object.entries(data.teamsWithUsers).forEach(([teamId, users], index) => {
    const teamName = data.teamNames[teamId];
    const teamColor = visuallyDistinctColors[index] || getRandomHexColor(teamName);
    finalTree.teamColorScheme[teamName] = teamColor;
    users.forEach((userId) => {
      teamColors[userId] = teamColor;
    });
  });

  // prepare entire tree with direct reports as children
  Object.keys(data.directReportsUpline)
    .map((id) => {
      calculatedTree[id] = {
        id,
        children: [],
      };

      return id;
    })
    .forEach((id) => {
      if (data.directReportsUpline[id]) {
        calculatedTree[data.directReportsUpline[id]].children.push(calculatedTree[id]);
      } else {
        finalTree.noUpline.push({
          id,
          userName: data.userNames[id],
          teamColor: teamColors[id],
          permissionsToolTip: genPermissionTooltip(id),
        });
      }
    });

  // find top node
  const topLevelNode = Object.values(calculatedTree).find((node) => {
    let hasNoUpline = false;
    let hasReports = false;
    hasReports = node.children.length > 0;
    hasNoUpline = data.directReportsUpline[node.id] === null;

    return hasReports && hasNoUpline;
  });

  if (!topLevelNode) return finalTree;

  const noUplineIndexToRemove = finalTree.noUpline.findIndex((node) => node.id === topLevelNode.id);
  if (noUplineIndexToRemove > -1) {
    finalTree.noUpline.splice(noUplineIndexToRemove, 1);
  }

  finalTree.rootNode = {
    id: topLevelNode.id,
    userName: data.userNames[topLevelNode.id],
    teamColor: teamColors[topLevelNode.id],
    permissionsToolTip: genPermissionTooltip(topLevelNode.id),
    children: [],
  };

  const buildFinalTree = (calculatedTreeNode, finalTreeNode = finalTree.rootNode) => {
    const { children } = calculatedTreeNode;

    children.forEach((child) => {
      finalTreeNode.children.push({
        id: child.id,
        userName: data.userNames[child.id],
        teamColor: teamColors[child.id],
        permissionsToolTip: genPermissionTooltip(child.id),
        children: [],
      });

      const referencedTreeNode = finalTreeNode.children.find(
        (nestedChild) => nestedChild.id === child.id
      );
      buildFinalTree(child, referencedTreeNode);
    });
  };

  buildFinalTree(topLevelNode);

  return finalTree;
};

const treeNodeStyles = {
  border: "1px solid black",
  display: "inline-block",
  borderRadius: "8px",
  padding: "4px 12px",
  whiteSpace: "nowrap",
  cursor: "help",
};

const TreeNodeLabel = ({ node, hoveredTeamColor }) => (
  <TooltipLight
    title={
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          whiteSpace: "nowrap",
        }}
      >
        {node.permissionsToolTip.map((string) => (
          <Typography fontSize={16}>{string}</Typography>
        ))}
      </Box>
    }
    variant="outline"
    placement="bottom"
    maxWidth="none"
  >
    <Box
      style={{
        ...treeNodeStyles,
        backgroundColor: node.teamColor,
        boxShadow: hoveredTeamColor === node.teamColor ? "5px 3px 3px black" : "none",
      }}
    >
      <Typography fontSize={16} fontWeight={600} letterSpacing={1.5}>
        {node.userName}
      </Typography>
    </Box>
  </TooltipLight>
);

const renderTree = (node, depth = 0, hoveredTeamColor) => {
  if (!node) return null;
  if (!depth)
    return (
      <Tree
        lineWidth="1px"
        lineColor="black"
        lineBorderRadius="10px"
        label={<TreeNodeLabel node={node} hoveredTeamColor={hoveredTeamColor} />}
      >
        {node.children.map((nestedNode) => renderTree(nestedNode, depth + 1, hoveredTeamColor))}
      </Tree>
    );
  return (
    <TreeNode label={<TreeNodeLabel node={node} hoveredTeamColor={hoveredTeamColor} />}>
      {node.children.map((nestedNode) => renderTree(nestedNode, depth + 1, hoveredTeamColor))}
    </TreeNode>
  );
};

function Hierarchy() {
  const [treeData, setTreeData] = useState(null);
  const [treeStructure, setTreeStructure] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState();
  const [hoveredTeamColor, setHoveredTeamColor] = useState(null);

  const fetchCompanies = async () => {
    try {
      const result = await getAPICall(FETCH_COMPANY_LIST, true);

      if (result?.data?.success) {
        setCompanyList([
          ...(result.data.data.map((company) => ({ id: company.id, name: company?.name })) || []),
        ]);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchHierarchy = async () => {
    try {
      const companyId = selectedCompany?.id;
      if (!companyId) return;

      const result = await getAPICall(`${FETCH_HIERARCHY}/${companyId}`, true);
      if (result?.data?.success) {
        setTreeData(result.data.data);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchHierarchy();
  }, [selectedCompany]);

  useEffect(() => {
    if (treeData) {
      setTreeStructure(createTreeFromHierarchy(treeData));
    }
  }, [treeData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox sx={{ pt: 3 }}>
        <Autocomplete
          fullWidth
          id="company-autocomplete"
          options={companyList}
          value={selectedCompany || null}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(_, value) => {
            setSelectedCompany(value);
          }}
          renderInput={(params) => <TextField {...params} label="Company" />}
        />
        {treeStructure && (
          <Box
            sx={{
              width: "100%",
              maxHeight: "600px",
              overflowX: "scroll",
              overflowY: "scroll",
              paddingBottom: "48px",
              paddingTop: "24px",
              border: "1px solid black",
              margin: "60px 24px 16px 0px",
            }}
          >
            {renderTree(treeStructure.rootNode, 0, hoveredTeamColor)}
          </Box>
        )}
        {treeStructure && (
          <Box
            sx={{
              marginTop: "50px",
              display: "flex",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                width: "50%",
              }}
            >
              <Typography variant="h4">Teams</Typography>
              <Box
                sx={{
                  marginTop: "16px",
                  display: "flex",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {Object.entries(treeStructure.teamColorScheme).map(([teamName, teamColor]) => (
                  <div
                    key={teamName}
                    style={{
                      backgroundColor: teamColor,
                      padding: "12px",
                      border: ".5px solid black",
                      cursor: "help",
                    }}
                    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                    onMouseOver={() => {
                      setHoveredTeamColor(teamColor);
                    }}
                    onMouseLeave={() => {
                      setHoveredTeamColor(null);
                    }}
                  >
                    {teamName}
                  </div>
                ))}
              </Box>
            </Box>
            <Box
              sx={{
                width: "50%",
              }}
            >
              <Typography variant="h4">No Upline</Typography>
              <Box
                sx={{
                  marginTop: "16px",
                  display: "flex",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {treeStructure.noUpline.map((node) => (
                  <TreeNodeLabel node={node} hoveredTeamColor={hoveredTeamColor} />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default Hierarchy;
